import { useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, RefreshCw, Send, UploadCloud } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type {
  Artifact,
  ArtifactPublication,
  ArtifactPublicationDriftState,
  ArtifactWorkItemLink,
  CurrentUser,
  GitHubWorkItem,
  GitHubWorkItemDetailResponse,
} from '@/lib/api';
import { checkDrift, getWorkItem, listArtifactWorkItemLinks, listPublications } from '@/lib/api';
import { publishArtifact } from '@/lib/artifact-publications-api';

interface ArtifactGithubPublicationPanelProps {
  artifact: Artifact;
  currentUser: CurrentUser | null;
}

interface ToastState {
  kind: 'success' | 'error';
  message: string;
}

const DRIFT_LABEL: Record<ArtifactPublicationDriftState, string> = {
  fresh: 'fresh',
  drifted: 'drifted',
  comment_deleted: 'comment deleted',
  verifying: 'verifying',
  superseded: 'superseded',
};

function repoIssueLabel(workItem: GitHubWorkItem): string {
  return `${workItem.githubRepoOwner}/${workItem.githubRepoName}#${String(workItem.githubIssueNumber)}`;
}

function formatDateTime(value: string | null): string {
  if (!value) return 'never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function truncateId(value: string): string {
  return value.slice(0, 8);
}

function publicationForWorkItem(
  publications: ArtifactPublication[],
  workItemId: string
): ArtifactPublication | null {
  let latest: ArtifactPublication | null = null;
  for (const publication of publications) {
    if (publication.workItemId !== workItemId) continue;
    if (!latest || Date.parse(publication.publishedAt) > Date.parse(latest.publishedAt)) {
      latest = publication;
    }
  }
  return latest;
}

function mutationErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export function ArtifactGithubPublicationPanel({
  artifact,
  currentUser,
}: ArtifactGithubPublicationPanelProps): React.ReactElement {
  const queryClient = useQueryClient();
  const canMutate =
    currentUser !== null && (currentUser.role === 'admin' || currentUser.id === artifact.ownerId);
  const [toast, setToast] = useState<ToastState | null>(null);

  const linksQuery = useQuery<ArtifactWorkItemLink[]>({
    queryKey: ['artifact', artifact.id, 'work-item-links'],
    queryFn: () => listArtifactWorkItemLinks(artifact.id),
  });

  const publicationsQuery = useQuery<ArtifactPublication[]>({
    queryKey: ['artifact', artifact.id, 'github-publications'],
    queryFn: () => listPublications(artifact.id),
  });

  const links = linksQuery.data ?? [];
  const publications = publicationsQuery.data ?? [];
  const workItemQueries = useQueries({
    queries: links.map(link => ({
      queryKey: ['github-work-item', link.workItemId] as const,
      queryFn: (): Promise<GitHubWorkItemDetailResponse> => getWorkItem(link.workItemId),
      staleTime: 30_000,
    })),
  });

  const publishMutation = useMutation({
    mutationFn: (workItemId: string) => publishArtifact(artifact.id, { workItemId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['artifact', artifact.id, 'github-publications'],
      });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'activity'] });
      setToast({ kind: 'success', message: 'Published to GitHub.' });
    },
    onError: error => {
      setToast({
        kind: 'error',
        message: mutationErrorMessage(error, 'Failed to publish to GitHub.'),
      });
    },
  });

  const republishMutation = useMutation({
    mutationFn: (workItemId: string) => publishArtifact(artifact.id, { workItemId, force: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['artifact', artifact.id, 'github-publications'],
      });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'activity'] });
      setToast({ kind: 'success', message: 'Republished to GitHub.' });
    },
    onError: error => {
      setToast({
        kind: 'error',
        message: mutationErrorMessage(error, 'Failed to republish to GitHub.'),
      });
    },
  });

  const checkDriftMutation = useMutation({
    mutationFn: (publicationId: string) => checkDrift(publicationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['artifact', artifact.id, 'github-publications'],
      });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'activity'] });
      setToast({ kind: 'success', message: 'Drift check complete.' });
    },
    onError: error => {
      setToast({
        kind: 'error',
        message: mutationErrorMessage(error, 'Failed to check drift.'),
      });
    },
  });

  if (linksQuery.isLoading || publicationsQuery.isLoading) {
    return <div className="text-xs text-text-secondary">Loading GitHub publications…</div>;
  }

  if (linksQuery.isError) {
    return (
      <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
        {linksQuery.error.message}
      </div>
    );
  }

  if (publicationsQuery.isError) {
    return (
      <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
        {publicationsQuery.error.message}
      </div>
    );
  }

  const hasApprovedSnapshot =
    artifact.status === 'approved' && artifact.currentApprovedSnapshotId !== null;

  return (
    <div className="flex flex-col gap-3 text-sm">
      {links.length === 0 ? (
        <span className="text-xs text-text-secondary">No linked GitHub work items yet.</span>
      ) : (
        <ul className="flex flex-col gap-2">
          {links.map((link, idx) => {
            const workItemQuery = workItemQueries[idx];
            const workItem = workItemQuery.data?.workItem;
            const publication = publicationForWorkItem(publications, link.workItemId);
            const hasPublication = publication !== null;
            const shouldRepublish =
              publication !== null &&
              artifact.currentApprovedSnapshotId !== null &&
              (publication.driftState === 'drifted' ||
                publication.driftState === 'superseded' ||
                publication.snapshotId !== artifact.currentApprovedSnapshotId);
            const isPublishing =
              publishMutation.isPending && publishMutation.variables === link.workItemId;
            const isRepublishing =
              republishMutation.isPending && republishMutation.variables === link.workItemId;
            const isChecking =
              publication !== null &&
              checkDriftMutation.isPending &&
              checkDriftMutation.variables === publication.id;

            return (
              <li
                key={link.id}
                className="flex flex-col gap-2 rounded-md border border-border bg-surface/40 p-2"
              >
                <div className="flex flex-col gap-2 md:flex-row md:items-center">
                  <div className="min-w-0 flex-1">
                    {workItemQuery.isLoading && (
                      <span className="text-xs text-text-secondary">Loading work item…</span>
                    )}
                    {workItemQuery.isError && (
                      <span className="font-mono text-xs text-text-secondary">
                        {link.workItemId} (cannot read)
                      </span>
                    )}
                    {workItem && (
                      <>
                        <span className="block truncate text-text-primary">{workItem.title}</span>
                        <span className="block truncate font-mono text-xs text-text-secondary">
                          {repoIssueLabel(workItem)}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {!hasPublication && (
                      <Button
                        type="button"
                        size="sm"
                        disabled={!canMutate || !hasApprovedSnapshot || publishMutation.isPending}
                        onClick={(): void => {
                          setToast(null);
                          publishMutation.mutate(link.workItemId);
                        }}
                        className="inline-flex items-center gap-1.5"
                      >
                        <Send className="size-4" />
                        {isPublishing ? 'Publishing…' : 'Publish'}
                      </Button>
                    )}
                    {shouldRepublish && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={!canMutate || republishMutation.isPending}
                        onClick={(): void => {
                          setToast(null);
                          republishMutation.mutate(link.workItemId);
                        }}
                        className="inline-flex items-center gap-1.5"
                      >
                        <UploadCloud className="size-4" />
                        {isRepublishing ? 'Republishing…' : 'Republish'}
                      </Button>
                    )}
                    {publication && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={!canMutate || checkDriftMutation.isPending}
                        onClick={(): void => {
                          setToast(null);
                          checkDriftMutation.mutate(publication.id);
                        }}
                        className="inline-flex items-center gap-1.5"
                      >
                        <RefreshCw className="size-4" />
                        {isChecking ? 'Checking…' : 'Check drift'}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-2 text-xs md:grid-cols-5">
                  <div>
                    <span className="block text-text-secondary">State</span>
                    <span className="font-mono text-text-primary">
                      {publication ? DRIFT_LABEL[publication.driftState] : 'not published'}
                    </span>
                  </div>
                  <div>
                    <span className="block text-text-secondary">Target</span>
                    <span className="font-mono text-text-primary">
                      {publication?.publicationTarget ?? 'managed_comment'}
                    </span>
                  </div>
                  <div>
                    <span className="block text-text-secondary">Last checked</span>
                    <span className="font-mono text-text-primary">
                      {formatDateTime(publication?.lastCheckedAt ?? null)}
                    </span>
                  </div>
                  <div>
                    <span className="block text-text-secondary">Snapshot</span>
                    <span className="font-mono text-text-primary">
                      {publication ? truncateId(publication.snapshotId) : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="block text-text-secondary">GitHub comment</span>
                    {publication?.externalUrl ? (
                      <a
                        href={publication.externalUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex max-w-full items-center gap-1 text-primary hover:underline"
                      >
                        <span className="truncate">Open comment</span>
                        <ExternalLink className="size-3 shrink-0" />
                      </a>
                    ) : (
                      <span className="font-mono text-text-secondary">-</span>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {toast && (
        <div
          role={toast.kind === 'success' ? 'status' : 'alert'}
          className={
            toast.kind === 'success'
              ? 'rounded-md bg-success/15 p-2 text-xs text-success'
              : 'rounded-md bg-error/15 p-2 text-xs text-error'
          }
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
